// 关键样式 - 同步加载
import 'virtual:windi-base.css';
import 'virtual:windi-components.css';
import 'virtual:windi-utilities.css';
import '/@/common/css/comm.less';
import '/@/design/index.less';

// 核心依赖
import { createApp } from 'vue';
import VueCookies from 'vue-cookies';
import App from './App.vue';
import { registerGlobComp } from '/@/components/registerGlobComp';
import { setupGlobDirectives } from '/@/directives';
import { setupI18n } from '/@/locales/setupI18n';
import { setupErrorHandle } from '/@/logics/error-handle';
import { initAppConfigStore } from '/@/logics/initAppConfig';
import { router, setupRouter } from '/@/router';
import { setupRouterGuard } from '/@/router/guard';
import { setupStore } from '/@/store';
import { dataFormat } from '/@/utils/validator';

// Vant 按需导入样式（只导入必要的）
import 'vant/es/dialog/style';
import 'vant/es/toast/style';
// 动态加载资源文件
function loadResource(url: string, type: 'script' | 'stylesheet'): Promise<void> {
  return new Promise((resolve, reject) => {
    if (type === 'script') {
      const script = document.createElement('script');
      script.src = url;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${url}`));
      document.head.appendChild(script);
    } else {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.onload = () => resolve();
      link.onerror = () => reject(new Error(`Failed to load stylesheet: ${url}`));
      document.head.appendChild(link);
    }
  });
}

// 延迟加载非关键资源
async function loadNonCriticalResources(app: ReturnType<typeof createApp>) {
  try {
    // 延迟加载图标字体 CSS（在应用挂载后异步加载）
    // CSS 文件可以直接使用动态导入
    await Promise.all([
      import('./assets/iconFont/iconfont.css'),
      import('./assets/newIcon/iconfont.css'),
    ]);
    
    // JS 文件需要动态创建 script 标签加载
    // 注意：这些 JS 文件通常只是注册图标，延迟加载不影响功能
    const iconFontJs = new URL('./assets/iconFont/iconfont.js', import.meta.url).href;
    const newIconJs = new URL('./assets/newIcon/iconfont.js', import.meta.url).href;
    
    await Promise.all([
      loadResource(iconFontJs, 'script'),
      loadResource(newIconJs, 'script'),
    ]);
  } catch (err) {
    console.warn('图标字体加载失败:', err);
  }

  try {
    // 延迟加载 Vant 全量样式（如果需要）
    await import('vant/lib/index.css');
  } catch {
    // 静默失败，因为已经导入了必要的样式
  }

  try {
    // 延迟加载 VueHashCalendar
    const [VueHashCalendarModule] = await Promise.all([
      import('vue3-hash-calendar'),
      import('vue3-hash-calendar/es/index.css'),
    ]);
    const VueHashCalendar = VueHashCalendarModule.default;
    if (VueHashCalendar) {
      app.use(VueHashCalendar);
    }
  } catch (err) {
    console.warn('VueHashCalendar 加载失败:', err);
  }

  try {
    // 延迟加载 ant-design-vue 组件（按需加载）
    const { componentsPlugin } = await import('./utils/antd');
    componentsPlugin(app);
  } catch (err) {
    console.warn('Ant Design Vue 组件加载失败:', err);
  }

  try {
    // 延迟注册图标
    await import('virtual:svg-icons-register');
  } catch {
    // 静默失败
  }
}

// requestIdleCallback 的 polyfill
function requestIdleCallbackPolyfill(callback: () => void, options?: { timeout?: number }) {
  const timeout = options?.timeout || 0;
  return setTimeout(callback, Math.min(timeout, 100));
}

async function bootstrap() {
  const app = createApp(App);
  
  // 基础配置
  app.config.globalProperties.$cookie = VueCookies;
  app.config.globalProperties.$dataFormat = dataFormat;
  
  // Configure store
  setupStore(app);

  // Initialize internal system configuration
  initAppConfigStore();

  // Register global components
  registerGlobComp(app);

  // Multilingual configuration - 优化：可以延迟加载非当前语言
  await setupI18n(app);

  // Configure routing
  setupRouter(app);

  // router-guard
  setupRouterGuard(router);

  // Register global directive
  setupGlobDirectives(app);

  // Configure global error handling
  setupErrorHandle(app);

  // 先挂载应用，让用户看到界面（关键优化：提前挂载）
  app.mount('#app');

  // 应用挂载后，异步加载非关键资源
  // 使用 requestIdleCallback 在浏览器空闲时加载，提升首屏性能
  const idleCallback = typeof requestIdleCallback !== 'undefined' 
    ? requestIdleCallback 
    : requestIdleCallbackPolyfill;
  
  idleCallback(() => {
    loadNonCriticalResources(app);
  }, { timeout: 2000 });
}

bootstrap();
